<template>
	<v-dialog v-model="isVisible" max-width="900" persistent>
		<v-card class="modal-card">
			<!-- Header -->
			<v-card-title class="modal-header" :style="`background-color: ${$appColors.primary};`">
				<div class="header-content">
					<v-icon color="white" size="28" class="mr-3">mdi-briefcase-check</v-icon>
					<span class="modal-title">Estado de Configuración de Puestos</span>
				</div>
				<div class="modal-close-btn" @click="close">
					<close-icon color="white"></close-icon>
				</div>
			</v-card-title>
			
			<v-card-text class="modal-content">
				<!-- Estado completo -->
				<div v-if="incompleteJobs.length === 0" class="success-state">
					<div class="success-icon-container">
						<v-icon :color="$appColors.success" size="64">mdi-check-circle</v-icon>
					</div>
					<h3 class="success-title" :style="`color: ${$appColors.success};`">
						¡Excelente trabajo!
					</h3>
					<p class="success-message" :style="`color: ${$appColors.info};`">
						Todos los puestos están configurados correctamente. El Manual de Perfiles de Puestos tendrá la máxima precisión.
					</p>
				</div>
				
				<!-- Puestos incompletos -->
				<div v-else class="incomplete-state">
					<div class="state-header">
						<div class="state-icon-container">
							<v-icon :color="$appColors.primary" size="32">mdi-alert-circle-outline</v-icon>
						</div>
						<div class="state-info">
							<h3 class="state-title" :style="`color: ${$appColors.primary};`">
								Configuración Pendiente
							</h3>
							<p class="state-subtitle" :style="`color: ${$appColors.info};`">
								{{ incompleteJobs.length }} {{ incompleteJobs.length === 1 ? 'puesto requiere' : 'puestos requieren' }} configuración adicional
							</p>
						</div>
					</div>

					<div class="jobs-list">
						<v-card
							v-for="job in incompleteJobs"
							:key="job.name"
							class="job-card"
							elevation="2"
						>
							<v-card-text class="job-content">
								<!-- Job header -->
								<div class="job-header">
									<div class="job-info">
										<h4 class="job-name" :style="`color: ${$appColors.secondary};`">
											{{ job.name }}
										</h4>
										<div class="job-progress-info">
											<span class="progress-percentage" :style="`color: ${$appColors.primary};`">
												{{ job.progress }}% completado
											</span>
											<span class="progress-fields" :style="`color: ${$appColors.info};`">
												{{ getCompletedFieldsCount(job) }}/6 campos
											</span>
										</div>
									</div>
									<div class="job-status-icon">
										<v-icon :color="getProgressIconColor(job.progress)" size="24">
											{{ getProgressIcon(job.progress) }}
										</v-icon>
									</div>
								</div>

								<!-- Progress bar -->
								<div class="progress-container">
									<v-progress-linear
										:value="job.progress"
										:color="getProgressColor(job.progress)"
										height="10"
										rounded
										class="progress-bar"
									></v-progress-linear>
								</div>

								<!-- Missing fields -->
								<div class="missing-fields">
									<h5 class="missing-fields-title" :style="`color: ${$appColors.secondary};`">
										Campos pendientes:
									</h5>
									<div class="fields-chips">
										<v-chip
											v-for="field in getMissingFields(job)"
											:key="field"
											small
											:color="$appColors.error"
											text-color="white"
											class="field-chip"
										>
											<v-icon left size="14">mdi-alert-circle</v-icon>
											{{ field }}
										</v-chip>
									</div>
								</div>
							</v-card-text>
						</v-card>
					</div>
					
					<div class="footer-message">
						<v-icon :color="$appColors.info" size="20" class="mr-2">mdi-information</v-icon>
						<span :style="`color: ${$appColors.info};`">
							Complete la configuración de estos puestos para obtener evaluaciones más precisas.
						</span>
					</div>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import {
	verifyJobIsCompleted,
	calculateJobProgress,
	getCompletedFieldsCount,
} from '@/modules/job-profile/helpers/helpers';
import CloseIcon from '@/modules/shared/components/icons/close-icon';

export default {
	name: 'IncompleteJobsModal',
	components: {
		CloseIcon,
	},
	data() {
		return {
			isVisible: false,
		};
	},
	props: {
		jobList: {
			type: Array,
			default: () => [],
		},
	},
	computed: {
		incompleteJobs() {
			if (!this.jobList || this.jobList.length === 0) {
				return [];
			}

			return this.jobList
				.filter(job => !verifyJobIsCompleted(job))
				.map(job => ({
					...job,
					progress: calculateJobProgress(job),
				}));
		},
	},
	methods: {
		open() {
			this.isVisible = true;
		},
		close() {
			this.isVisible = false;
		},
		getProgressColor(progress) {
			if (progress >= 80) return 'success';
			if (progress >= 50) return 'warning';
			return 'error';
		},
		getProgressIcon(progress) {
			if (progress >= 80) return 'mdi-check-circle';
			if (progress >= 50) return 'mdi-clock-alert';
			return 'mdi-alert-circle';
		},
		getProgressIconColor(progress) {
			if (progress >= 80) return this.$appColors.success;
			if (progress >= 50) return this.$appColors.warning;
			return this.$appColors.error;
		},
		getCompletedFieldsCount(job) {
			return getCompletedFieldsCount(job);
		},
		getMissingFields(job) {
			const missingFields = [];

			// Verificar verbos
			const hasVerbs = ['highCommand', 'mediumCommand', 'lowCommand'].some((levelKey) => {
				const level = job[levelKey];
				return level && Object.values(level).some(value => value === true);
			});
			if (!hasVerbs) {
				missingFields.push('Verbos de acción');
			}

			// Verificar otros campos
			if (!job.role || (typeof job.role === 'object' && Object.keys(job.role).length === 0)) {
				missingFields.push('Rol');
			}
			if (!job.title || (typeof job.title === 'object' && Object.keys(job.title).length === 0)) {
				missingFields.push('Título');
			}
			if (
				!job.experience ||
				(typeof job.experience === 'object' && Object.keys(job.experience).length === 0)
			) {
				missingFields.push('Experiencia');
			}
			if (
				!job.englishLevel ||
				(typeof job.englishLevel === 'object' && Object.keys(job.englishLevel).length === 0)
			) {
				missingFields.push('Nivel de inglés');
			}
			if (!job.quantityPerson || job.quantityPerson <= 0) {
				missingFields.push('Cantidad de personas');
			}

			return missingFields;
		},
	},
};
</script>

<style lang="scss" scoped>
.modal-card {
	border-radius: 12px !important;
	overflow: hidden;
}

.modal-header {
	padding: 20px 24px !important;
	border-radius: 0 !important;
	border: none !important;
	display: flex !important;
	justify-content: space-between !important;
	align-items: center !important;
	
	.header-content {
		display: flex;
		align-items: center;
		flex: 1;
	}
	
	.modal-title {
		color: white !important;
		font-size: 1.25rem;
		font-weight: 600;
		letter-spacing: 0.5px;
	}
	
	.modal-close-btn {
		cursor: pointer;
		width: 32px;
		height: 32px;
		padding: 0;
		border-radius: 50%;
		transition: all 0.2s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		background-color: transparent;
		color: white !important;
		
		&:hover {
			background-color: rgba(255, 255, 255, 0.15);
			transform: scale(1.1);
		}
	}
}

.modal-content {
	padding: 32px 24px !important;
}

// Success state
.success-state {
	text-align: center;
	padding: 40px 20px;
	
	.success-icon-container {
		margin-bottom: 20px;
		animation: pulse 2s infinite;
	}
	
	.success-title {
		font-size: 1.5rem;
		font-weight: 600;
		margin-bottom: 12px;
	}
	
	.success-message {
		font-size: 1rem;
		line-height: 1.5;
		max-width: 400px;
		margin: 0 auto;
	}
}

// Incomplete state
.incomplete-state {
	.state-header {
		display: flex;
		align-items: center;
		margin-bottom: 24px;
		padding: 20px;
		background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
		border-radius: 12px;
		border-left: 4px solid var(--app-color-primary);
		
		.state-icon-container {
			margin-right: 16px;
		}
		
		.state-title {
			font-size: 1.25rem;
			font-weight: 600;
			margin-bottom: 4px;
		}
		
		.state-subtitle {
			font-size: 0.95rem;
			margin: 0;
		}
	}
	
	.jobs-list {
		margin-bottom: 24px;
		
		.job-card {
			margin-bottom: 16px;
			border-radius: 12px !important;
			transition: all 0.3s ease;
			border-left: 4px solid var(--app-color-secondary);
			
			&:hover {
				transform: translateY(-2px);
				box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
			}
			
			&:last-child {
				margin-bottom: 0;
			}
		}
		
		.job-content {
			padding: 20px !important;
		}
		
		.job-header {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			margin-bottom: 16px;
			
			.job-info {
				flex: 1;
			}
			
			.job-name {
				font-size: 1.1rem;
				font-weight: 600;
				margin-bottom: 8px;
			}
			
			.job-progress-info {
				display: flex;
				gap: 12px;
				align-items: center;
				
				.progress-percentage {
					font-weight: 600;
					font-size: 0.9rem;
				}
				
				.progress-fields {
					font-size: 0.85rem;
				}
			}
			
			.job-status-icon {
				margin-left: 16px;
			}
		}
		
		.progress-container {
			margin-bottom: 20px;
			position: relative;
			z-index: 1;
			
			.progress-bar {
				border-radius: 6px !important;
				display: block !important;
				position: static !important;
				
				// Mantener el progress bar en su lugar sin position absolute
				::v-deep .v-progress-linear__background {
					border-radius: 6px !important;
				}
				
				::v-deep .v-progress-linear__buffer {
					border-radius: 6px !important;
				}
				
				::v-deep .v-progress-linear__determinate {
					border-radius: 6px !important;
					transition: width 0.3s ease !important;
				}
				
				// Evitar que se mueva con hover
				::v-deep .v-progress-linear {
					position: static !important;
				}
			}
		}
		
		.missing-fields {
			.missing-fields-title {
				font-size: 0.95rem;
				font-weight: 600;
				margin-bottom: 12px;
			}
			
			.fields-chips {
				display: flex;
				flex-wrap: wrap;
				gap: 8px;
				
				.field-chip {
					transition: all 0.2s ease;
					
					&:hover {
						transform: scale(1.05);
					}
				}
			}
		}
	}
	
	.footer-message {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 16px;
		background: rgba(0, 0, 0, 0.02);
		border-radius: 8px;
		font-size: 0.9rem;
	}
}

.modal-footer {
	padding: 20px 24px !important;
	background: #f8f9fa;
	border-top: 1px solid #e9ecef;
	
	.close-button {
		font-weight: 600;
		padding: 12px 24px !important;
		border-radius: 8px !important;
		text-transform: none !important;
		letter-spacing: 0.5px;
		transition: all 0.2s ease;
		
		&:hover {
			transform: translateY(-1px);
		}
	}
}

// Animations
@keyframes pulse {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.05);
	}
	100% {
		transform: scale(1);
	}
}

// Responsive
@media (max-width: 768px) {
	.modal-content {
		padding: 20px 16px !important;
	}
	
	.state-header {
		flex-direction: column;
		text-align: center;
		
		.state-icon-container {
			margin-right: 0;
			margin-bottom: 12px;
		}
	}
	
	.job-header {
		flex-direction: column;
		
		.job-status-icon {
			margin-left: 0;
			margin-top: 12px;
			align-self: center;
		}
	}
	
	.job-progress-info {
		flex-direction: column;
		align-items: flex-start !important;
		gap: 4px !important;
	}
}
</style>



// WEBPACK FOOTER //
// src/modules/job-profile/components/jobs-profile/incomplete-jobs-modal.vue